import Accordion from '@material-ui/core/Accordion'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import { useTheme } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import ImageAccordion from '../../../../assets/accordion/bg.png'
import { usePreloadedImage } from '../../../../hooks/use-preloaded-image'
import { useMainPageFaqSectionStyles } from './main-page-faq-section.styles'

export const MainPageFaqSection: React.FC = () => {
  const theme = useTheme()
  const classes = useMainPageFaqSectionStyles()
  const [expanded, setExpanded] = React.useState<string | false>(false)
  const { t } = useTranslation()
  const bgReady = usePreloadedImage(ImageAccordion)
  const [loaderMounted, setLoaderMounted] = React.useState(true)

  React.useEffect(() => {
    if (!bgReady) setLoaderMounted(true)
  }, [bgReady])

  const onLoaderTransitionEnd = (
    e: React.TransitionEvent<HTMLDivElement>
  ) => {
    if (e.target !== e.currentTarget) return
    if (e.propertyName !== 'opacity' || !bgReady) return
    setLoaderMounted(false)
  }

  const handleChange =
    (panel: string) =>
    (_event: React.ChangeEvent<unknown>, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }

  return (
    <div className={classes.accordionSection}>
      <div
        className={[
          classes.sectionBgImage,
          bgReady && classes.sectionBgImageVisible,
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ backgroundImage: `url(${ImageAccordion})` }}
      />
      {loaderMounted && (
        <div
          className={[
            classes.sectionBgLoader,
            bgReady && classes.sectionBgLoaderHidden,
          ]
            .filter(Boolean)
            .join(' ')}
          onTransitionEnd={onLoaderTransitionEnd}
        >
          <CircularProgress color="primary" />
        </div>
      )}
      <div className={classes.accordionOverlay}></div>
      <div className={classes.accordionHolder}>
        <div className={classes.accordionBlock}>
          <div className={classes.accordionTitle}>
            {t('mainPage.faq.title')}
          </div>
          <Accordion
            expanded={expanded === 'panel1'}
            onChange={handleChange('panel1')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.generalSettings')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t(
                    'mainPage.faq.content.generalSettings.whatIsProsphora.question'
                  )}
                </strong>
                <br />
                {t(
                  'mainPage.faq.content.generalSettings.whatIsProsphora.answer'
                )}
                <br />
                <br />
                <strong>
                  2.{' '}
                  {t(
                    'mainPage.faq.content.generalSettings.howItWorks.question'
                  )}
                </strong>
                <br />
                {(
                  t(
                    'mainPage.faq.content.generalSettings.howItWorks.points',
                    { returnObjects: true }
                  ) as string[]
                ).map((point: string, index: number) => (
                  <React.Fragment key={index}>
                    <>
                      • <Trans i18nKey={point} />
                      <br />
                    </>
                  </React.Fragment>
                ))}
                <br />
                <strong>
                  3.{' '}
                  {t(
                    'mainPage.faq.content.generalSettings.whoCanUse.question'
                  )}
                </strong>
                <br />
                {(
                  t('mainPage.faq.content.generalSettings.whoCanUse.points', {
                    returnObjects: true,
                  }) as string[]
                ).map((point: string, index: number) => (
                  <React.Fragment key={index}>
                    • {point}
                    <br />
                  </React.Fragment>
                ))}
                <br />
                <strong>
                  4.{' '}
                  {t(
                    'mainPage.faq.content.generalSettings.nominateNonprofit.question'
                  )}
                </strong>
                <br />
                {t(
                  'mainPage.faq.content.generalSettings.nominateNonprofit.answer'
                )}
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel2'}
            onChange={handleChange('panel2')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel2bh-content"
              id="panel2bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.accountSetup')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t(
                    'mainPage.faq.content.accountSetup.needAccount.question'
                  )}
                </strong>
                <br />
                {t('mainPage.faq.content.accountSetup.needAccount.answer')}
                <br />
                <br />
                <strong>
                  2.{' '}
                  {t(
                    'mainPage.faq.content.accountSetup.howToRegister.question'
                  )}
                </strong>
                <br />
                <Trans
                  i18nKey={t(
                    'mainPage.faq.content.accountSetup.howToRegister.answer'
                  )}
                  components={{
                    registerLink: (
                      <a
                        href="prosphora.com/register"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          textDecoration: 'underline',
                          color: theme.palette.common.white,
                        }}
                      >
                        prosphora.com/register
                      </a>
                    ),
                  }}
                />
                <br />
                <br />
                <strong>
                  3.{' '}
                  {t(
                    'mainPage.faq.content.accountSetup.registerAsNonprofit.question'
                  )}
                </strong>
                <br />
                {t(
                  'mainPage.faq.content.accountSetup.registerAsNonprofit.answer'
                )}
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel3'}
            onChange={handleChange('panel3')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel3bh-content"
              id="panel3bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.listingItems')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t('mainPage.faq.content.listingItems.howToList.question')}
                </strong>
                <br />
                {(
                  t('mainPage.faq.content.listingItems.howToList.points', {
                    returnObjects: true,
                  }) as string[]
                ).map((point: string, index: number) => (
                  <React.Fragment key={index}>
                    <>
                      • <Trans i18nKey={point} />
                      <br />
                    </>
                  </React.Fragment>
                ))}
                <br />
                <br />
                <strong>
                  2.{' '}
                  {t(
                    'mainPage.faq.content.listingItems.restrictions.question'
                  )}
                </strong>
                <br />
                {t('mainPage.faq.content.listingItems.restrictions.answer')}
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel4'}
            onChange={handleChange('panel4')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel4bh-content"
              id="panel4bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.purchasingItems')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t(
                    'mainPage.faq.content.purchasingItems.howToPurchase.question'
                  )}
                </strong>
                <br />
                {(
                  t(
                    'mainPage.faq.content.purchasingItems.howToPurchase.points',
                    { returnObjects: true }
                  ) as string[]
                ).map((point: string, index: number) => (
                  <React.Fragment key={index}>
                    • {point}
                    <br />
                  </React.Fragment>
                ))}
                <br />
                <br />
                <strong>
                  2.{' '}
                  {t(
                    'mainPage.faq.content.purchasingItems.contactSeller.question'
                  )}
                </strong>
                <br />
                {t(
                  'mainPage.faq.content.purchasingItems.contactSeller.answer'
                )}
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel5'}
            onChange={handleChange('panel5')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel5bh-content"
              id="panel5bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.paymentProcessing')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t(
                    'mainPage.faq.content.paymentProcessing.paymentMethods.question'
                  )}
                </strong>
                <br />
                <Trans
                  i18nKey={t(
                    'mainPage.faq.content.paymentProcessing.paymentMethods.answer'
                  )}
                />
                <br />
                <br />
                <strong>
                  2.{' '}
                  {t(
                    'mainPage.faq.content.paymentProcessing.paymentSecurity.question'
                  )}
                </strong>
                <br />
                {t(
                  'mainPage.faq.content.paymentProcessing.paymentSecurity.answer'
                )}
                <br />
                <br />
                <strong>
                  3.{' '}
                  {t(
                    'mainPage.faq.content.paymentProcessing.areThereFees.question'
                  )}
                </strong>
                <br />
                {t(
                  'mainPage.faq.content.paymentProcessing.areThereFees.answer'
                )}
                <br />
                <br />
                {t(
                  'mainPage.faq.content.paymentProcessing.processingFees.question'
                )}
                <br />
                {t(
                  'mainPage.faq.content.paymentProcessing.processingFees.answer'
                )}
                <br />
                <br />
                <Trans
                  i18nKey={t(
                    'mainPage.faq.content.paymentProcessing.platformFees.question'
                  )}
                  components={{ em: <em /> }}
                />
                <br />
                {t(
                  'mainPage.faq.content.paymentProcessing.platformFees.answer'
                )}
                <br />
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel6'}
            onChange={handleChange('panel6')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel6bh-content"
              id="panel6bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.localPickup')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t(
                    'mainPage.faq.content.localPickup.availability.question'
                  )}
                </strong>
                <br />
                <Trans
                  i18nKey={t(
                    'mainPage.faq.content.localPickup.availability.answer'
                  )}
                />
                <br />
                <br />
                <strong>
                  2.{' '}
                  {t(
                    'mainPage.faq.content.localPickup.futureOption.question'
                  )}
                </strong>
                <br />
                {t('mainPage.faq.content.localPickup.futureOption.answer')}
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel7'}
            onChange={handleChange('panel7')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel7bh-content"
              id="panel7bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.donations')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t(
                    'mainPage.faq.content.donations.chooseDifferentNonprofit.question'
                  )}
                </strong>
                <br />
                {t(
                  'mainPage.faq.content.donations.chooseDifferentNonprofit.answer'
                )}
                <br />
                <br />
                <strong>
                  2.{' '}
                  {t(
                    'mainPage.faq.content.donations.chooseDifferentNonprofit.question'
                  )}
                </strong>
                <br />
                {t(
                  'mainPage.faq.content.donations.chooseDifferentNonprofit.answer'
                )}
                <br />
                <br />
                <strong>
                  3.{' '}
                  {t('mainPage.faq.content.donations.taxDeductible.question')}
                </strong>
                <br />
                {t('mainPage.faq.content.donations.taxDeductible.answer')}
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel8'}
            onChange={handleChange('panel8')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel8bh-content"
              id="panel8bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.technicalSupport')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t(
                    'mainPage.faq.content.technicalSupport.forgotPassword.question'
                  )}
                </strong>
                <br />
                <Trans
                  i18nKey={t(
                    'mainPage.faq.content.technicalSupport.forgotPassword.answer'
                  )}
                />
                <br />
                <br />
                <strong>
                  2.{' '}
                  {t(
                    'mainPage.faq.content.technicalSupport.contactSupport.question'
                  )}
                </strong>
                <br />
                {t(
                  'mainPage.faq.content.technicalSupport.contactSupport.answer'
                )}
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel9'}
            onChange={handleChange('panel9')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel9bh-content"
              id="panel9bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.itemGuidelinesAndDonationImpact')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t(
                    'mainPage.faq.content.itemGuidelines.typeOfItems.question'
                  )}
                </strong>
                <br />
                <Trans
                  i18nKey={t(
                    'mainPage.faq.content.itemGuidelines.typeOfItems.answer'
                  )}
                />
                <br />
                <br />
                {t(
                  'mainPage.faq.content.itemGuidelines.bestTypesOfItems.header'
                )}
                <br />
                {(
                  t(
                    'mainPage.faq.content.itemGuidelines.bestTypesOfItems.points',
                    { returnObjects: true }
                  ) as string[]
                ).map((point: string, index: number) => (
                  <React.Fragment key={index}>
                    • {point}
                    <br />
                  </React.Fragment>
                ))}
                <br />
                <br />
                {t('mainPage.faq.content.itemGuidelines.notes.header')}
                <br />
                {(
                  t('mainPage.faq.content.itemGuidelines.notes.points', {
                    returnObjects: true,
                  }) as string[]
                ).map((point: string, index: number) => (
                  <React.Fragment key={index}>
                    {point}
                    <br />
                  </React.Fragment>
                ))}
                <br />
                <br />
                {t(
                  'mainPage.faq.content.itemGuidelines.avoidDonatingItems.header'
                )}
                <br />
                {(
                  t(
                    'mainPage.faq.content.itemGuidelines.avoidDonatingItems.points',
                    { returnObjects: true }
                  ) as string[]
                ).map((point: string, index: number) => (
                  <React.Fragment key={index}>
                    • {point}
                    <br />
                  </React.Fragment>
                ))}
                <br />
                {t(
                  'mainPage.faq.content.itemGuidelines.avoidDonatingItems.footer'
                )}
                <br />
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel10'}
            onChange={handleChange('panel10')}
            className={classes.customAccordionBg}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon className={classes.customIcon} />
              }
              aria-controls="panel10bh-content"
              id="panel10bh-header"
            >
              <Typography className={classes.heading}>
                {t('mainPage.faq.sections.shipping')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={classes.accordionText}>
                <strong>
                  1.{' '}
                  {t(
                    'mainPage.faq.content.shipping.howShippingWorks.question'
                  )}
                </strong>
                <br />
                <Trans
                  i18nKey={t(
                    'mainPage.faq.content.shipping.howShippingWorks.answer'
                  )}
                />
                <br />
                <br />
                <strong>
                  2.{' '}
                  {t(
                    'mainPage.faq.content.shipping.paymentResponsibility.question'
                  )}
                </strong>
                <br />
                <Trans
                  i18nKey={t(
                    'mainPage.faq.content.shipping.paymentResponsibility.answer'
                  )}
                />
                <br />
                <br />
                <strong>
                  3.{' '}
                  {t('mainPage.faq.content.shipping.sellerActions.question')}
                </strong>
                <br />
                {t('mainPage.faq.content.shipping.sellerActions.header')}
                <br />
                <br />
                {(
                  t('mainPage.faq.content.shipping.sellerActions.points', {
                    returnObjects: true,
                  }) as string[]
                ).map((point: string, index: number) => (
                  <React.Fragment key={index}>
                    • <Trans i18nKey={point} />
                    <br />
                  </React.Fragment>
                ))}
                <br />
                <strong>
                  4.{' '}
                  {t('mainPage.faq.content.shipping.restrictions.question')}
                </strong>
                <br />
                <Trans
                  i18nKey={t(
                    'mainPage.faq.content.shipping.restrictions.answer'
                  )}
                />
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
    </div>
  )
}
